import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Search, Download, Trash2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';

export default function UsersTab({ users, stats, onExtendSubscription, onExtendUserTrial, onDeleteUser, onExportCSV, onLoadUsers, onLoadStats }) {
  const { adminFetch, API_URL } = useAdmin();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async (userId) => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/users/${userId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('User deleted successfully');
        setDeleteConfirm(null);
        onLoadUsers();
        onLoadStats();
      } else {
        toast.error('Failed to delete user');
      }
    } catch (error) {
      toast.error('Error deleting user');
    }
  };

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-600 text-white"
            data-testid="users-search-input"
          />
        </div>
        <Button onClick={onExportCSV} variant="outline" className="border-slate-600 text-slate-300">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <Card className="bg-slate-800 border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Registered</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Last Login</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Workouts</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredUsers.map(user => (
                <tr key={user.user_id} className="hover:bg-slate-700/30">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-white font-medium">{user.name || 'Unknown'}</p>
                      <p className="text-slate-400 text-sm">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-sm">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-sm">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-sm">{user.total_workouts || 0}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs">
                      {user.subscription_status === 'active' ? (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full">Active</span>
                      ) : user.subscription_status === 'trial' ? (
                        <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-full">Trial</span>
                      ) : (
                        <span className="px-2 py-1 bg-slate-500/20 text-slate-400 rounded-full">Expired</span>
                      )}
                      {user.subscription_end && (
                        <p className="text-slate-500 mt-1">
                          {new Date(user.subscription_end) > new Date() ? 'Until' : 'Ended'}: {new Date(user.subscription_end).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => onExtendSubscription(user.user_id, 30, 'trial')}
                        className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20 text-xs px-2"
                        title="Extend Trial +30 days"
                        data-testid={`extend-trial-${user.user_id}`}
                      >+30d Trial</Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => onExtendSubscription(user.user_id, 365, 'active')}
                        className="text-green-400 hover:text-green-300 hover:bg-green-500/20 text-xs px-2"
                        title="Give 1 Year Active"
                        data-testid={`extend-year-${user.user_id}`}
                      >+1yr Pro</Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setDeleteConfirm(user)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        title="Delete User"
                        data-testid={`delete-user-${user.user_id}`}
                      ><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1 pt-1 border-t border-slate-700">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => onExtendUserTrial(user.user_id)}
                        className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20 text-[10px] px-1.5 py-0.5 h-auto"
                        title="Extend user's trial by 14 days"
                        data-testid={`extend-trial-14d-${user.user_id}`}
                      >+14d Trial</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-slate-400">No users found</div>
          )}
        </div>
      </Card>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-md mx-4">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-400" />
                <h3 className="text-lg font-semibold text-white">Confirm Delete</h3>
              </div>
              <p className="text-slate-300">
                Are you sure you want to delete user <strong className="text-white">{deleteConfirm.name || deleteConfirm.email}</strong>?
              </p>
              <p className="text-red-400 text-sm">
                This will permanently delete all their data including workouts, nutrition logs, and training plans.
              </p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-slate-600 text-slate-300" data-testid="cancel-delete-user">
                  Cancel
                </Button>
                <Button onClick={() => handleDelete(deleteConfirm.user_id)} className="bg-red-600 hover:bg-red-500 text-white" data-testid="confirm-delete-user">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete User
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
